<template>
    <settings-wrapper title="Instance settings" section="General">
        <v-skeleton-loader v-if="fetchingInstances" type="card" class="mt-4" />
        <v-alert v-else-if="!currentInstance" type="info" text dense class="mt-4">No instance is available for configuration.</v-alert>

        <template v-if="currentInstance">
            <v-row>
                <v-col>
                    <v-card>
                        <v-card-title>Instance details</v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col>
                                    <label>Name</label>
                                    <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ instanceLongName }}</v-sheet>
                                </v-col>
                                <v-col>
                                    <label>Description</label>
                                    <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ currentInstanceDescription }}</v-sheet>
                                </v-col>
                            </v-row>
                        </v-card-text>
                        <v-card-actions>
                            <instance-edit-dialog
                                v-if="currentInstance && isInstanceEditor"
                                :instance="currentInstance"
                                :space-id="resolvedSpaceId"
                                @updated="fetchSpaceInstances">
                                <template v-slot:activator="{ on, attrs, disabled }">
                                    <v-btn color="primary" text v-bind="attrs" v-on="on" :disabled="disabled">Edit</v-btn>
                                </template>
                            </instance-edit-dialog>
                        </v-card-actions>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <h3 class="error--text">Danger Zone</h3>
                </v-col>
            </v-row>

            <v-row v-if="isSpaceAdmin">
                <v-col cols="6">
                    <v-card>
                        <v-card-title>Delete instance</v-card-title>
                        <v-card-actions>
                            <instance-delete-dialog
                                v-if="canDeleteInstance"
                                :instance-id="parseInt(currentInstance.iid, 10)"
                                :instance-name="currentInstance.long_id"
                                :master-instance-id="masterInstance.iid.toString()"
                                :master-development-snapshot-id="masterInstance.dev_snid.toString()">
                                <template v-slot:activator="{ attrs, disabled, open }">
                                    <v-btn color="error" text v-bind="attrs" :disabled="disabled" @click="open">Delete instance</v-btn>
                                </template>
                            </instance-delete-dialog>
                            <v-btn v-else color="error" text disabled>Delete instance</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-col>
            </v-row>
        </template>
    </settings-wrapper>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { isAdminRole, isEditorRole, isMasterInstance, isMasterOrDistributedInstance } from '@/utils'
import SettingsWrapper from '@/components/SettingsWrapper.vue'
import InstanceEditDialog from '@/modules/instance/components/InstanceEditDialog.vue'
import InstanceDeleteDialog from '@/modules/instance/components/TheInstanceDeleteDialog.vue'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as instanceErrors from '@/modules/instance/errors'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'

const { getBackendErrorMessage } = createBackendErrorHandler(instanceErrors, lifecycleErrors, permissionsErrors)

export default {
    name: 'InstanceConfiguration',
    props: {
        spaceId: {
            type: [String, Number],
            default: null
        }
    },
    components: {
        SettingsWrapper,
        InstanceEditDialog,
        InstanceDeleteDialog
    },
    data() {
        return {
            instances: [],
            fetchingInstances: false,
            selectedInstanceId: null
        }
    },
    methods: {
        async fetchSpaceInstances() {
            if (!this.resolvedSpaceId) {
                this.instances = []
                this.selectedInstanceId = null
                return
            }

            this.fetchingInstances = true
            try {
                const { data } = await this.$axios.get(`spaces/${this.resolvedSpaceId}/instances`)
                this.instances = data || []
                this.syncSelectedInstance()
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, error.response?.data?.debug || 'Failed to fetch instances.')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            } finally {
                this.fetchingInstances = false
            }
        },
        syncSelectedInstance() {
            const routeInstanceId = this.$route.params.iid
            if (routeInstanceId && this.instances.some(instance => instance.iid.toString() === routeInstanceId.toString())) {
                this.selectedInstanceId = routeInstanceId
                return
            }

            if (!this.selectedInstanceId || !this.instances.some(instance => instance.iid.toString() === this.selectedInstanceId.toString())) {
                this.selectedInstanceId = this.instances[0]?.iid || null
            }
        },
        onSelectedInstanceChange(instanceId) {
            if (!instanceId || !this.resolvedSpaceId || !this.activeOrgId) {
                return
            }

            if (this.isConfigInstanceRoute) {
                if (
                    this.$route.params.iid &&
                    this.$route.params.iid.toString() === instanceId.toString() &&
                    this.$route.params.sid &&
                    this.$route.params.sid.toString() === this.resolvedSpaceId.toString()
                ) {
                    return
                }

                this.$router.replace({
                    name: this.$route.name,
                    params: {
                        ...this.$route.params,
                        sid: this.resolvedSpaceId,
                        iid: instanceId
                    },
                    query: { ...this.$route.query }
                })
                return
            }

            if (this.$route.params.iid && this.$route.params.iid.toString() === instanceId.toString()) {
                return
            }

            const selectedSpace = this.spacesWithPrimarySnapshots.find(space => space.sid.toString() === this.resolvedSpaceId.toString())
            const snid = this.$route.params.snid || selectedSpace?.snid
            if (!snid) {
                return
            }

            const params = {
                oid: this.activeOrgId,
                sid: this.resolvedSpaceId,
                iid: instanceId,
                snid
            }
            if (this.$route.params.tab) {
                params.tab = this.$route.params.tab
            }

            this.$router.push({
                name: 'instance-configuration',
                params,
                query: { ...this.$route.query }
            })
        }
    },
    computed: {
        ...mapState('orgStore', ['spacesWithPrimarySnapshots']),
        ...mapGetters('spaceStore', ['isSpaceAdmin']),
        activeOrgId() {
            return this.$route.params.oid
        },
        resolvedSpaceId() {
            return this.spaceId || this.$route.params.sid
        },
        isConfigInstanceRoute() {
            return this.$route.name?.startsWith('config-instance')
        },
        isInstanceEditor() {
            const role = this.currentInstance?.role
            return isAdminRole(role) || isEditorRole(role)
        },
        instanceLongName() {
            return this.currentInstance?.long_id || 'Instance name not available'
        },
        currentInstanceDescription() {
            return this.currentInstance?.description || 'No description available for this instance'
        },
        currentInstance() {
            return this.instances.find(instance => instance.iid.toString() === this.selectedInstanceId?.toString()) || null
        },
        masterInstance() {
            return this.instances.find(instance => isMasterInstance(instance.long_id)) || null
        },
        isDeleteInstanceDisabled() {
            if (!this.currentInstance?.long_id) {
                return true
            }
            return isMasterOrDistributedInstance(this.currentInstance.long_id)
        },
        canDeleteInstance() {
            return Boolean(
                this.isSpaceAdmin &&
                    this.currentInstance &&
                    this.masterInstance?.iid !== undefined &&
                    this.masterInstance?.dev_snid !== undefined &&
                    !this.isDeleteInstanceDisabled
            )
        }
    },
    watch: {
        spacesWithPrimarySnapshots: {
            immediate: true,
            handler(spaces) {
                if (this.isConfigInstanceRoute && !this.$route.params.sid && Array.isArray(spaces) && spaces.length) {
                    this.$router.replace({
                        name: this.$route.name,
                        params: {
                            ...this.$route.params,
                            sid: spaces[0].sid
                        },
                        query: { ...this.$route.query }
                    })
                }
            }
        },
        resolvedSpaceId: {
            immediate: true,
            handler() {
                this.fetchSpaceInstances()
            }
        },
        '$route.params.iid'() {
            this.syncSelectedInstance()
        },
        selectedInstanceId(instanceId) {
            this.onSelectedInstanceChange(instanceId)
        }
    }
}
</script>
